import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { FadeUp, Overline } from "../components/Reveal";

const rows = [
  { size: "S", chest: "96 \u2013 100", length: "70", sleeve: "20", shoulder: "44" },
  { size: "M", chest: "101 \u2013 106", length: "72", sleeve: "21", shoulder: "46" },
  { size: "L", chest: "107 \u2013 112", length: "74", sleeve: "22", shoulder: "48" },
  { size: "XL", chest: "113 \u2013 118", length: "76", sleeve: "23", shoulder: "50" },
];

const measures = [
  {
    title: "Chest",
    body: "Measure around the fullest part of your chest, keeping the tape level and relaxed under the arms.",
  },
  {
    title: "Length",
    body: "From the highest point of the shoulder, straight down to the hem of the garment.",
  },
  {
    title: "Sleeve",
    body: "From the shoulder seam to the edge of the sleeve, with the arm resting naturally.",
  },
  {
    title: "Shoulder",
    body: "Across the back, from the tip of one shoulder seam to the other.",
  },
];

const SizeGuide = () => {
  return (
    <div data-testid="size-guide-page" className="min-h-screen pt-28 md:pt-36 pb-28">
      <div className="max-w-[900px] mx-auto px-6 md:px-12">
        <Link
          to="/"
          data-testid="size-guide-back-link"
          className="inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-zinc-500 hover:text-gold transition-colors duration-300"
        >
          <ArrowLeft size={13} /> The House
        </Link>

        <FadeUp>
          <div className="mt-10">
            <Overline>Information</Overline>
            <h1 className="mt-4 font-serif text-4xl md:text-6xl font-light text-[#F5F5F5] leading-tight">
              Size Guide
            </h1>
            <p className="mt-6 max-w-xl text-sm md:text-base text-zinc-400 leading-relaxed">
              A piece should feel like it was always yours. Find your fit below, or write to the
              house and we will guide you personally.
            </p>
          </div>
        </FadeUp>

        <FadeUp delay={0.1}>
          <div className="mt-14">
            <p className="text-xs tracking-[0.3em] uppercase text-gold mb-5">Measurements &middot; centimetres</p>
            <div className="overflow-x-auto border border-white/10" data-testid="size-table">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-white/10 bg-[#141414]">
                    {["Size", "Chest", "Length", "Sleeve", "Shoulder"].map((h) => (
                      <th
                        key={h}
                        className="px-5 py-4 text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-normal"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.size}
                      data-testid={`size-row-${r.size}`}
                      className="border-b border-white/5 last:border-0 hover:bg-[#141414] transition-colors duration-300"
                    >
                      <td className="px-5 py-4 font-serif text-xl text-gold">{r.size}</td>
                      <td className="px-5 py-4 text-sm text-zinc-300">{r.chest}</td>
                      <td className="px-5 py-4 text-sm text-zinc-300">{r.length}</td>
                      <td className="px-5 py-4 text-sm text-zinc-300">{r.sleeve}</td>
                      <td className="px-5 py-4 text-sm text-zinc-300">{r.shoulder}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-xs text-zinc-600 leading-relaxed">
              Measurements are of the garment and may vary by a centimetre, as is natural to
              hand-finished pieces. If you are between sizes, we suggest sizing up for an
              easier drape.
            </p>
          </div>
        </FadeUp>

        <div className="mt-16">
          <FadeUp>
            <p className="text-xs tracking-[0.3em] uppercase text-gold mb-8">How to Measure</p>
          </FadeUp>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-10">
            {measures.map((m, i) => (
              <FadeUp key={m.title} delay={0.05}>
                <div data-testid={`measure-${i}`} className="border-t border-white/10 pt-6">
                  <h2 className="font-serif text-xl md:text-2xl font-light text-[#F5F5F5]">
                    {m.title}
                  </h2>
                  <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{m.body}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>

        <FadeUp delay={0.1}>
          <div className="mt-16 border-t border-maroon/40 pt-10">
            <p className="text-sm text-zinc-400 leading-relaxed">
              Still unsure of your fit? Write to us at{" "}
              <a
                href="mailto:vinceninfo@gmail.com"
                data-testid="size-guide-email"
                className="text-gold hover:text-gold-hover transition-colors duration-300"
              >
                vinceninfo@gmail.com
              </a>{" "}
              and a member of the house will help you choose.
            </p>
          </div>
        </FadeUp>
      </div>
    </div>
  );
};

export default SizeGuide;
